"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCQLIntervalEndpoints = exports.execute = void 0;
const cql_execution_1 = require("cql-execution");
const ValueSetHelper_1 = require("./ValueSetHelper");
const ValueSetResolver_1 = require("./ValueSetResolver");
const CustomErrors_1 = require("../types/errors/CustomErrors");
const ELMInfoCache_1 = require("../helpers/elm/ELMInfoCache");
async function execute(measure, measureBundle, patientSource, options, valueSetCache = [], debugObject) {
    // Determine "root" library by looking at which lib is referenced by populations, and pull out the ELM
    var _a;
    // check for any missing valuesets
    const valueSets = [];
    const newCache = [];
    // Pass in existing cache to attempt any missing resolutions
    const missingVS = (0, ValueSetHelper_1.getMissingDependentValuesets)(measureBundle, options.useEffectiveDataRequirements, [
        ...valueSetCache
    ]);
    if (missingVS.length > 0) {
        if (!options.vsAPIKey || options.vsAPIKey.length == 0) {
            throw new CustomErrors_1.UnexpectedResource(`Missing the following valuesets: ${missingVS.join(', ')}, and no API key was provided to resolve them`);
        }
        const vsr = new ValueSetResolver_1.ValueSetResolver(options.vsAPIKey || '');
        const [expansions, errorMessages] = await vsr.getExpansionForValuesetUrls(missingVS);
        if (errorMessages.length > 0) {
            throw new Error(errorMessages.join('\n'));
        }
        valueSets.push(...expansions);
        // Update cache to include new expansions
        if (options.useValueSetCaching) {
            newCache.push(...expansions);
        }
    }
    (_a = measureBundle.entry) === null || _a === void 0 ? void 0 : _a.forEach(e => {
        var _a;
        if (((_a = e.resource) === null || _a === void 0 ? void 0 : _a.resourceType) === 'ValueSet') {
            valueSets.push(e.resource);
        }
    });
    // Include provided ValueSets in code service
    // These can be provided directly as an argument or via the caching behavior
    valueSets.push(...valueSetCache);
    const { cqls, rootLibIdentifier, elmJSONs, codeService, rep, vsMap } = (0, ELMInfoCache_1.retrieveELMInfo)(measure, measureBundle, valueSets, options.useElmJsonsCaching);
    const { startCql, endCql } = getCQLIntervalEndpoints(options);
    const parameters = { 'Measurement Period': new cql_execution_1.Interval(startCql, endCql) };
    const executionDateTime = cql_execution_1.DateTime.fromJSDate(new Date(), 0);
    const lib = rep.resolve(rootLibIdentifier.id, rootLibIdentifier.version);
    const executor = new cql_execution_1.Executor(lib, codeService, parameters);
    let results;
    try {
        results = await executor.exec(patientSource, executionDateTime);
    }
    catch (e) {
        if (e instanceof Error) {
            e.message = `The following error occurred in the cql-execution engine: ${e.message}`;
            if (e instanceof TypeError) {
                e.message +=
                    '\n\n\t- Inspect the content of the ELM and ensure the data types in the expressions are correct\n\n';
            }
        }
        throw e;
    }
    // Map evaluated resource from engine to the raw FHIR json
    Object.keys(results.patientEvaluatedRecords).forEach(patientId => {
        results.patientEvaluatedRecords[patientId] = results.patientEvaluatedRecords[patientId].map((r) => r._json);
    });
    if (debugObject && options.enableDebugOutput) {
        debugObject.elm = elmJSONs;
        debugObject.cql = cqls;
        debugObject.vs = vsMap;
        debugObject.rawResults = results;
    }
    return Object.assign({ rawResults: results, elmLibraries: elmJSONs, mainLibraryName: rootLibIdentifier.id, parameters: parameters }, (options.useValueSetCaching && { valueSetCache: newCache }));
}
exports.execute = execute;
/**
 * Takes in the calculation options and returns start and end dates to create a cql interval
 * @param options calculationOptions passed in by the user
 * @returns {startCql: Date, endCql: Date}, the start and end date of the calculationOptions
 */
function getCQLIntervalEndpoints(options) {
    // Measure datetime stuff
    let start;
    let end;
    if (options.measurementPeriodStart) {
        start = (0, ValueSetHelper_1.parseTimeStringAsUTC)(options.measurementPeriodStart);
    }
    else {
        start = new Date('2019-01-01');
    }
    if (options.measurementPeriodEnd) {
        end = (0, ValueSetHelper_1.parseTimeStringAsUTC)(options.measurementPeriodEnd);
    }
    else {
        end = new Date('2019-12-31');
    }
    const startCql = cql_execution_1.DateTime.fromJSDate(start, 0); // No timezone offset for start
    const endCql = cql_execution_1.DateTime.fromJSDate(end, 0); // No timezone offset for stop
    return { startCql, endCql };
}
exports.getCQLIntervalEndpoints = getCQLIntervalEndpoints;
//# sourceMappingURL=Execution.js.map